import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/auth/useAuth';
import type { InkChargeInfo, InkUsageSummary, InkWalletBalance } from '@/types/ink';

type InkContextType = {
  balanceInk: number | null;
  summary: InkUsageSummary | null;
  inkLoading: boolean;
  inkError: string | null;
  refreshInk: () => Promise<void>;
  applyInkResponse: (ink?: InkChargeInfo | null) => void;
};

export const InkContext = createContext<InkContextType | undefined>(undefined);

type InkProviderProps = {
  children: ReactNode;
};

function readBalance(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

export function InkProvider({ children }: InkProviderProps) {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const [summary, setSummary] = useState<InkUsageSummary | null>(null);
  const [balanceInk, setBalanceInk] = useState<number | null>(null);
  const [inkLoading, setInkLoading] = useState(false);
  const [inkError, setInkError] = useState<string | null>(null);

  const refreshInk = useCallback(async () => {
    if (!userId) {
      setSummary(null);
      setBalanceInk(null);
      setInkError(null);
      return;
    }

    setInkLoading(true);
    setInkError(null);

    try {
      const { data: summaryData, error: summaryError } = await supabase
        .from('ink_usage_summary')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (summaryError) {
        console.error('Ink summary load error:', summaryError);
      }

      if (summaryData) {
        const nextSummary = summaryData as InkUsageSummary;
        setSummary(nextSummary);
        setBalanceInk(readBalance(nextSummary.balance_ink));
        return;
      }

      const { data: walletData, error: walletError } = await supabase
        .from('ink_wallets')
        .select('balance_ink')
        .eq('user_id', userId)
        .maybeSingle();

      if (walletError) {
        throw walletError;
      }

      const wallet = walletData as InkWalletBalance | null;
      setSummary(null);
      setBalanceInk(readBalance(wallet?.balance_ink));
    } catch (error) {
      console.error('Ink load exception:', error);
      setInkError('Не удалось загрузить баланс чернил');
    } finally {
      setInkLoading(false);
    }
  }, [userId]);

  const applyInkResponse = useCallback((ink?: InkChargeInfo | null) => {
    const nextBalance = readBalance(ink?.balance_after_ink);

    if (nextBalance === null) {
      return;
    }

    setBalanceInk(nextBalance);
    setSummary((currentSummary) => (
      currentSummary ? { ...currentSummary, balance_ink: nextBalance } : currentSummary
    ));
  }, []);

  useEffect(() => {
    // Real async side-effect: load the ink balance when the user changes (refreshInk is
    // useCallback([userId])). The loading/error state set synchronously inside refreshInk is
    // inherent to the fetch, not state derivable at render.
    // eslint-disable-next-line react-hooks/set-state-in-effect -- async ink balance fetch; loading state is inherent to the side-effect
    void refreshInk();
  }, [refreshInk]);

  const value = useMemo<InkContextType>(() => ({
    balanceInk,
    summary,
    inkLoading,
    inkError,
    refreshInk,
    applyInkResponse,
  }), [balanceInk, summary, inkLoading, inkError, refreshInk, applyInkResponse]);

  return <InkContext.Provider value={value}>{children}</InkContext.Provider>;
}
